import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthCtx, useCartCtx, useDarkMode } from '../../App';
import Logo from '../Logo';

// Catalogue dropdown — hover/focus-driven menu with the standard catalog plus
// the gamified Bulk Vault. Closes on blur or after a short hover-out delay.
function CatalogueMenu() {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const show = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  const hide = () => { closeTimer.current = setTimeout(() => setOpen(false), 120); };

  return (
    <div className="relative" onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        className="flex items-center gap-1 text-gray-600 hover:text-tea-600 font-medium transition-colors dark:text-slate-300 dark:hover:text-tea-400"
      >
        Catalogue
        <svg className={'w-4 h-4 transition-transform ' + (open ? 'rotate-180' : '')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-2 w-64 rounded-2xl border border-tea-100 bg-white py-2 z-50 dark:bg-slate-900 dark:border-slate-700"
          style={{ boxShadow: '0 10px 30px rgba(31,167,232,0.12)' }}
          role="menu"
        >
          <Link
            to="/shop"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 hover:bg-tea-50 dark:hover:bg-slate-800 transition-colors rounded-xl mx-1"
            role="menuitem"
          >
            <span className="block font-semibold text-gray-900 dark:text-slate-100">Full Catalog</span>
            <span className="block text-xs text-gray-500 dark:text-slate-400">Every research peptide, by the unit</span>
          </Link>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout, isMember } = useAuthCtx();
  const { count } = useCartCtx();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const openerRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/');
  };

  // Focus trap + Esc-to-close for the mobile drawer
  useEffect(() => {
    if (!menuOpen) return;
    const root = menuRef.current;
    const focusables = root?.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
    focusables?.[0]?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { setMenuOpen(false); openerRef.current?.focus(); }
      if (e.key === 'Tab' && focusables?.length) {
        const first = focusables[0], last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      {/* Skip link — first tab stop; lets keyboard/screen-reader users jump past
          the nav straight to the page's <main id="main-content">. Visually hidden
          until focused. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-3 focus:left-3 focus:px-4 focus:py-2 focus:rounded-lg focus:bg-tea-600 focus:text-white focus:font-semibold focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Promo banner — glowing blue */}
      <div
        className="border-b border-blue-300 text-white text-xs font-semibold animate-tt-blue-glow"
        style={{
          backgroundImage: 'linear-gradient(90deg, #60a5fa 0%, #3b82f6 25%, #2563eb 50%, #3b82f6 75%, #60a5fa 100%)',
          backgroundSize: '200% 100%',
          boxShadow: '0 0 18px rgba(59, 130, 246, 0.65), inset 0 0 12px rgba(255, 255, 255, 0.4)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-center text-center">
          <span className="tracking-wide"><strong>USA SHIPPING</strong> — 1 Day Handling</span>
        </div>
      </div>

      {/* Floating navbar — white pill sitting on the gradient background */}
      <div className="sticky top-0 z-50 px-4 sm:px-6 py-2.5 dark:bg-slate-950">
        <nav
          className="max-w-7xl mx-auto bg-white dark:bg-slate-900 rounded-2xl"
          style={{ boxShadow: '0 4px 24px rgba(31,167,232,0.10), 0 1px 4px rgba(0,0,0,0.04)' }}
        >
          <div className="px-5 sm:px-6">
            <div className="flex items-center h-14 md:h-16">
              {/* Hamburger — mobile, left side */}
              <button
                ref={openerRef}
                onClick={() => setMenuOpen(true)}
                aria-label="Open menu"
                aria-expanded={menuOpen}
                className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-xl text-gray-500 hover:bg-tea-50 hover:text-tea-600 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors mr-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" viewBox="0 0 24 24">
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              </button>

              {/* Desktop nav links — left */}
              <div className="hidden md:flex items-center gap-6 flex-1">
                <CatalogueMenu />
                {user && (
                  isMember ? (
                    <Link to="/membership" className="flex items-center gap-1 font-bold text-amber-600 hover:text-amber-700 transition-colors">
                      <span>★</span> VIP
                    </Link>
                  ) : (
                    <Link to="/membership" className="flex items-center gap-1 font-semibold text-amber-600 hover:text-amber-700 transition-colors">
                      Become VIP
                    </Link>
                  )
                )}
                {user && (
                  <Link to="/dashboard" className="text-gray-600 hover:text-tea-600 font-medium dark:text-slate-300 dark:hover:text-tea-400 transition-colors">Dashboard</Link>
                )}
                {user?.role === 'admin' && (
                  <>
                    <Link to="/admin/users" className="text-gray-600 hover:text-tea-600 font-medium dark:text-slate-300 dark:hover:text-tea-400 transition-colors">Users</Link>
                    <Link to="/admin" className="text-gray-600 hover:text-tea-600 font-medium dark:text-slate-300 dark:hover:text-tea-400 transition-colors">Admin</Link>
                  </>
                )}
              </div>

              {/* Logo — centered */}
              <div className="flex-1 md:flex-none flex justify-center md:absolute md:left-1/2 md:-translate-x-1/2">
                <Link to="/" className="flex items-center gap-2 shrink-0">
                  <Logo className="text-xl" />
                  <span className="hidden lg:inline text-xs text-gray-400 font-medium border-l border-gray-200 pl-2 ml-1 dark:text-slate-500 dark:border-slate-700">
                    Research peptides
                  </span>
                </Link>
              </div>

              {/* Right section */}
              <div className="flex items-center gap-2 md:gap-3 flex-1 justify-end">
                {/* Dark mode toggle — desktop only */}
                <button
                  onClick={toggleDarkMode}
                  className="hidden md:flex p-2 rounded-xl text-gray-400 hover:text-tea-600 hover:bg-tea-50 dark:text-slate-400 dark:hover:text-tea-400 dark:hover:bg-slate-800 transition-colors"
                  aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {darkMode ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M18.364 18.364l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </button>

                {/* Desktop: user name + logout OR login/signup */}
                {user ? (
                  <div className="hidden md:flex items-center gap-3">
                    <Link to="/account" className="text-gray-600 hover:text-tea-600 font-medium text-sm dark:text-slate-300 dark:hover:text-tea-400 transition-colors">
                      {user.firstName}
                    </Link>
                    <button onClick={handleLogout} className="btn-secondary text-sm px-4 py-2">
                      Logout
                    </button>
                  </div>
                ) : (
                  <div className="hidden md:flex items-center gap-2">
                    <Link to="/login"    className="btn-secondary text-sm px-4 py-2">Login</Link>
                    <Link to="/register" className="btn-primary  text-sm px-4 py-2">Sign Up</Link>
                  </div>
                )}

                {/* Cart icon — always visible */}
                <Link to="/cart" className="relative p-2 rounded-xl text-gray-500 hover:text-tea-600 hover:bg-tea-50 transition-colors dark:text-slate-300 dark:hover:text-tea-400 dark:hover:bg-slate-800" aria-label="Cart">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  {count > 0 && (
                    <span className="absolute -top-1 -right-1 bg-tea-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                      {count}
                    </span>
                  )}
                </Link>
              </div>
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile drawer overlay */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <button
            aria-label="Close menu"
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
          />
          {/* Drawer panel */}
          <div
            ref={menuRef}
            className="relative ml-auto w-80 max-w-[85vw] h-full bg-white dark:bg-slate-900 flex flex-col overflow-y-auto"
            style={{ boxShadow: '-8px 0 40px rgba(31,167,232,0.12)' }}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-tea-50 dark:border-slate-700">
              <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-2">
                <Logo className="text-lg" />
              </Link>
              <button
                onClick={() => setMenuOpen(false)}
                className="p-2 rounded-xl text-gray-400 hover:bg-tea-50 hover:text-tea-600 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Nav links */}
            <div className="flex-1 px-4 py-4 space-y-1">
              <Link to="/shop" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-tea-50 hover:text-tea-600 font-medium dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                Catalogue
              </Link>
              {user && (
                <Link to="/membership" onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-amber-700 hover:bg-amber-50 font-bold dark:text-amber-400 dark:hover:bg-amber-950/20 transition-colors">
                  {isMember ? '★ VIP Membership' : 'Become a VIP'}
                </Link>
              )}
              {user && (
                <Link to="/dashboard" onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-tea-50 hover:text-tea-600 font-medium dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                  Dashboard
                </Link>
              )}
              {user?.role === 'admin' && (
                <>
                  <Link to="/admin/users" onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-tea-50 hover:text-tea-600 font-medium dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                    Users
                  </Link>
                  <Link to="/admin" onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-tea-50 hover:text-tea-600 font-medium dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                    Admin
                  </Link>
                </>
              )}
            </div>

            {/* Preferences + auth at the bottom */}
            <div className="px-4 py-4 border-t border-tea-50 dark:border-slate-700 space-y-3">
              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-100 dark:border-slate-700 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-tea-50 dark:hover:bg-slate-800 transition-colors"
              >
                <span>{darkMode ? 'Light mode' : 'Dark mode'}</span>
                {darkMode ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M18.364 18.364l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Auth actions */}
              {user ? (
                <div className="space-y-2">
                  <Link to="/account" onClick={() => setMenuOpen(false)}
                    className="block w-full text-center btn-secondary text-sm py-2.5">
                    My Account
                  </Link>
                  <button onClick={handleLogout} className="w-full btn-secondary text-sm py-2.5 text-red-600 border-red-200 hover:bg-red-50">
                    Logout
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Link to="/login" onClick={() => setMenuOpen(false)} className="block w-full text-center btn-secondary text-sm py-2.5">
                    Login
                  </Link>
                  <Link to="/register" onClick={() => setMenuOpen(false)} className="block w-full text-center btn-primary text-sm py-2.5">
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
